import { PageProps } from '@/types';
import { Head, Link } from "@inertiajs/react";
import React, { useState, useMemo } from "react";
import PageLayout from "@/Layouts/PageLayout";
import PrimaryButton from "@/Components/PrimaryButton";
import PricingFaqs from "@/Pages/Pricing/PricingFaqs";
import useTranslations from "@/hooks/useTranslations";
import Popover from "@/Components/Popover";
import CreditsUsageBreakdown from "@/Components/CreditsUsageBreakdown";

type PricingPageProps = {
    pricing: Record<string, number>,
    freeHours: number,
    maxFileSize: number,
    maxFileSizePaid: number,
    hasCustomPricing: boolean,
}

export default function PricingPage({ pricing, freeHours, maxFileSize, maxFileSizePaid, hasCustomPricing, auth }: PageProps<PricingPageProps>) {
    const { __ } = useTranslations();
    const tierHours = Object.keys(pricing).map(Number);
    const minPurchaseHours = Math.min(...tierHours);
    const maxPurchaseHours = Math.max(...tierHours);
    const isSignedIn = !!auth.user;

    // Quadratic curve gives the low tiers enough slider travel to be selectable when the top tier is large
    const sliderSteps = 1000;
    const [sliderPosition, setSliderPosition] = useState(0);
    const totalHours = Math.round(freeHours + Math.pow(sliderPosition / sliderSteps, 2) * maxPurchaseHours);
    const sliderFillPercent = (sliderPosition / sliderSteps) * 100;

    const getHourRate = (hours: number) => {
        if (!pricing) return 0;
        const purchaseHours = Math.max(0, hours - freeHours);
        if (purchaseHours < minPurchaseHours && purchaseHours > 0) return pricing[minPurchaseHours.toString()];
        if (purchaseHours === 0) return 0;

        const tiers = Object.keys(pricing).map(Number).sort((a, b) => b - a);
        return pricing[tiers.find(tier => purchaseHours >= tier) || Math.min(...tiers)];
    };

    const purchaseHours = totalHours <= freeHours ? 0 : Math.max(minPurchaseHours, totalHours - freeHours);
    const actualTotalHours = purchaseHours === 0 ? freeHours : purchaseHours + freeHours;
    const totalCost = useMemo(() => {
        return purchaseHours * getHourRate(actualTotalHours);
    }, [actualTotalHours, pricing]);

    const isFree = totalHours <= freeHours;

    return (
    <PageLayout header={null}>
        <Head title={__('pricing.meta.title')}>
            <meta name="description" content={__('pricing.meta.description', { free_hours: freeHours })} />
        </Head>

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 ${isSignedIn ? 'pt-12' : '0'}`}>
            {/* Hero Section */}
            <div className="text-center mb-16">
                <h1 className="text-3xl md:text-4xl  text-gray-900 dark:text-gray-100 mb-4">
                    {__('pricing.hero.title')}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl px-4 md:px-0 mx-auto">
                    {__('pricing.hero.subtitle', { free_hours: freeHours })}
                </p>
            </div>


            <div className="max-w-2xl mx-auto mb-16">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 p-8">

                    <div className="mb-8">
                        <div className="relative mb-6">
                            <div className="flex flex-col justify-center items-center mb-4">
                                <div className="text-blue-600 dark:text-blue-400 px-4 pt-2 rounded-lg text-4xl font-bold">
                                    {actualTotalHours}
                                </div>
                                <span className="text-gray-900 dark:text-gray-100 flex items-center gap-1">
                                    {__('pricing.calculator.processing_hours')}
                                    <Popover trigger="hover" placement="bottom" content={<CreditsUsageBreakdown hours={actualTotalHours} />}>
                                        <button type="button" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </Popover>
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max={sliderSteps}
                                step="1"
                                value={sliderPosition}
                                onChange={(e) => setSliderPosition(parseInt(e.target.value))}
                                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                style={{
                                    background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${sliderFillPercent}%, #E5E7EB ${sliderFillPercent}%, #E5E7EB 100%)`
                                }}
                            />
                            <div className="flex justify-between text-sm text-gray-500 mt-2">
                                <span>{__('pricing.calculator.hours_free', { hours: freeHours })}</span>
                                <span>{__('pricing.calculator.hours_plus', { hours: maxPurchaseHours })}</span>
                            </div>
                        </div>


                        <div className="text-center mb-8">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                {isFree ? __('pricing.calculator.free') : `${totalCost.toFixed(2)}`}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">
                                {isFree ? (
                                    <span>{__('pricing.calculator.pricing_breakdown.free_start', { free_hours: freeHours })}</span>
                                ) : (
                                    <span>{__('pricing.calculator.pricing_breakdown.regular_purchase', {
                                        hours: purchaseHours,
                                        rate: getHourRate(actualTotalHours).toFixed(2),
                                        free_hours: freeHours
                                    })}</span>
                                )}
                            </div>
                        </div>


                        <div className="text-center">
                            <Link href={route(isSignedIn? 'billing.credits' : 'register')}>
                                <PrimaryButton className="w-full py-3 text-lg">
                                    {__('pricing.calculator.get_started')}
                                </PrimaryButton>
                            </Link>
                        </div>
                    </div>


                    <div className="border-t dark:border-gray-700 pt-6 ">
                        <div className="grid md:grid-cols-2 gap-8 md:gap-0 md:ml-10">

                            <div className="md:border-r dark:border-gray-700 md:mr-10">
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                                            <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium px-2 py-1 rounded mr-2">
                                                {__('pricing.features.free_tier.badge')}
                                            </span>
                                   <span>{__('pricing.features.free_tier.title', { hours: freeHours })}</span>
                                </h3>
                                <ul className="space-y-2 text-base text-gray-600 dark:text-gray-400">
                                    <li className="flex items-center mr-5">
                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.free_tier.high_quality')}</span>
                                    </li>
                                    <li className="flex items-center mr-5">
                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.free_tier.language_support')}</span>
                                    </li>
                                    <li className="flex items-center mr-5">
                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.free_tier.file_size_limit', { size: maxFileSize })}</span>
                                    </li>
                                    <li className="flex items-center mr-5">
                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.free_tier.subtitle_editor')}</span>
                                    </li>
                                    <li className="flex items-center mr-5">
                                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.free_tier.no_credit_card')}</span>
                                    </li>
                                </ul>
                            </div>


                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                                            <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium px-2 py-1 rounded mr-2">
                                                {__('pricing.features.paid_tier.badge')}
                                            </span>
                                    <span>{__('pricing.features.paid_tier.title')}</span>
                                </h3>
                                <ul className="space-y-2 text-base text-gray-600 dark:text-gray-400">
                                    <li className="flex items-center">
                                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.paid_tier.remote_imports')}</span>
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.paid_tier.concurrent_extractions')}</span>
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.paid_tier.file_size_limit', { size: maxFileSizePaid })}</span>
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.paid_tier.ai_translations')}</span>
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{__('pricing.features.paid_tier.video_export')}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="bg-gray-50 dark:bg-gray-800 py-8 md:py-12 my-12 md:my-16 rounded-lg">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 md:mb-8 text-center">
                        {__('pricing.volume_pricing.title')}
                    </h2>
                    <p className="text-center text-gray-600 dark:text-gray-400 mb-6 md:mb-8">
                        {__('pricing.volume_pricing.subtitle')}
                    </p>
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow divide-y divide-gray-100 dark:divide-gray-700 md:bg-transparent md:dark:bg-transparent md:shadow-none md:divide-y-0 md:flex md:flex-wrap md:justify-center md:gap-4">
                        {Object.entries(pricing || {}).map(([hours, rate]) => (
                            <div key={hours} className="flex items-center justify-between px-4 py-3 md:block md:w-36 md:p-4 md:bg-white md:dark:bg-gray-900 md:rounded-lg md:shadow md:text-center">
                                <div className="font-semibold md:text-lg md:font-bold text-gray-900 dark:text-gray-100">{__('pricing.volume_pricing.hours_format', { hours })}</div>
                                <div className="text-blue-600 dark:text-blue-400 font-semibold">{__('pricing.volume_pricing.rate_format', { rate: rate.toFixed(2) })}</div>
                            </div>
                        ))}
                    </div>
                    {!hasCustomPricing && (
                        <p className="text-center text-gray-500 dark:text-gray-400 mt-6 text-sm">
                            {__('pricing.volume_pricing.contact_for_volume')}
                        </p>
                    )}
                </div>
            </section>

            <PricingFaqs
                minimumPurchase={minPurchaseHours}
                freeHours={freeHours}
            />

            <div className="text-center py-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                    {__('pricing.cta.title')}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {__('pricing.cta.subtitle', { free_hours: freeHours })}
                </p>
                <Link href={route(isSignedIn? 'billing.credits' : 'register')}>
                    <PrimaryButton className="px-8 py-3 text-lg">
                        {__('pricing.cta.get_started')}
                    </PrimaryButton>
                </Link>
            </div>
        </div>
    </PageLayout>
    )
}
